import React, { useState, useMemo, useCallback, useRef } from 'react';
import { BuyerEntry } from './dataloaders';
import { 
    processBuyerEntries, 
    getTotalTickets, 
    createSeededRandom, 
    getRandomInt, 
    findBuyerByTicketNumber,
    BuyerEntryWithLottery
} from './ticketUtils';

interface GewinnerZiehenProps {
    buyerData?: BuyerEntry[];
    randomnessSeed?: string;
}

interface Winner {
    ticketNumber: number;
    buyer: BuyerEntryWithLottery;
    drawOrder: number;
}

const GewinnerZiehen: React.FC<GewinnerZiehenProps> = ({ 
    buyerData = [],
    randomnessSeed = ''
}) => {
    const [winners, setWinners] = useState<Winner[]>([]);
    // Use ref to store the random generator since it's a function and we need to persist it between renders
    const randomGeneratorRef = useRef<(() => number) | null>(null);

    // Process buyer data to get lottery entries with ticket ranges
    const processedEntries = useMemo(() => {
        return processBuyerEntries(buyerData);
    }, [buyerData]);

    // Calculate total tickets
    const totalTickets = useMemo(() => {
        return getTotalTickets(processedEntries);
    }, [processedEntries]);

    // Get all drawn ticket numbers to avoid duplicates
    const drawnTicketNumbers = useMemo(() => {
        return new Set(winners.map(w => w.ticketNumber));
    }, [winners]);

    // Check if we can draw more winners
    const canDrawMore = totalTickets > 0 && winners.length < totalTickets;

    // Draw a winner
    const drawWinner = useCallback(() => {
        if (!canDrawMore || !randomnessSeed) return;

        // Initialize random generator on first draw
        if (!randomGeneratorRef.current) {
            randomGeneratorRef.current = createSeededRandom(randomnessSeed);
        }
        const rng = randomGeneratorRef.current;

        // Try to find a unique ticket number (not already drawn)
        // Note: We use a retry loop instead of Fisher-Yates shuffle because:
        // 1. Typical lottery draws only a small number of winners from a large pool
        // 2. Collision probability is very low until most tickets are drawn
        // 3. The maxAttempts guard prevents infinite loops in edge cases
        let ticketNumber: number;
        let attempts = 0;
        const maxAttempts = totalTickets * 10; // Safety limit

        do {
            ticketNumber = getRandomInt(rng, 1, totalTickets);
            attempts++;
        } while (drawnTicketNumbers.has(ticketNumber) && attempts < maxAttempts);

        if (attempts >= maxAttempts) {
            console.error('Could not find unique ticket number after max attempts');
            return;
        }

        const buyer = findBuyerByTicketNumber(processedEntries, ticketNumber);
        if (!buyer) {
            console.error('Could not find buyer for ticket number:', ticketNumber);
            return;
        }

        const newWinner: Winner = {
            ticketNumber,
            buyer,
            drawOrder: winners.length + 1
        };

        setWinners(prev => [...prev, newWinner]);
    }, [canDrawMore, randomnessSeed, totalTickets, drawnTicketNumbers, processedEntries, winners.length]);

    // Reset all drawn winners
    const resetWinners = useCallback(() => {
        setWinners([]);
        randomGeneratorRef.current = null;
    }, []);

    // Render message if no data loaded
    if (buyerData.length === 0) {
        return (
            <div className="step-content">
                <h2>Gewinner ziehen</h2>
                <p style={{ color: '#6c757d' }}>
                    Bitte laden Sie zuerst Daten im ersten Schritt.
                </p>
            </div>
        );
    }

    // Render message if no seed
    if (!randomnessSeed) {
        return (
            <div className="step-content">
                <h2>Gewinner ziehen</h2>
                <p style={{ color: '#dc3545' }}>
                    Kein Zufallsseed verfügbar. Bitte laden Sie die Daten erneut.
                </p>
            </div>
        );
    }

    return (
        <div className="step-content">
            <h2>Gewinner ziehen</h2>
            
            {/* Info section with seed and total tickets */}
            <div style={{ 
                marginBottom: '24px', 
                padding: '16px', 
                backgroundColor: '#e7f3ff', 
                borderRadius: '8px',
                border: '1px solid #b8daff'
            }}>
                <div style={{ marginBottom: '12px' }}>
                    <strong>🎲 Zufallsseed:</strong>
                    <code style={{ 
                        marginLeft: '8px',
                        padding: '4px 8px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontFamily: 'monospace',
                        wordBreak: 'break-all'
                    }}>
                        {randomnessSeed.substring(0, 16)}...
                    </code>
                </div>
                <div>
                    <strong>🎫 Anzahl Lose im Lostopf:</strong>
                    <span style={{ 
                        marginLeft: '8px',
                        padding: '4px 12px',
                        backgroundColor: '#d4edda',
                        borderRadius: '4px',
                        color: '#155724',
                        fontWeight: 600
                    }}>
                        {totalTickets}
                    </span>
                </div>
            </div>

            {/* Draw button */}
            <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                marginBottom: '32px' 
            }}>
                <button
                    onClick={drawWinner}
                    disabled={!canDrawMore}
                    style={{
                        padding: '20px 48px',
                        fontSize: '18px',
                        fontWeight: 600,
                        backgroundColor: canDrawMore ? '#28a745' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: canDrawMore ? 'pointer' : 'not-allowed',
                        boxShadow: canDrawMore ? '0 4px 6px rgba(40, 167, 69, 0.3)' : 'none',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                        if (canDrawMore) {
                            e.currentTarget.style.backgroundColor = '#218838';
                            e.currentTarget.style.transform = 'scale(1.02)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (canDrawMore) {
                            e.currentTarget.style.backgroundColor = '#28a745';
                            e.currentTarget.style.transform = 'scale(1)';
                        }
                    }}
                >
                    {winners.length === 0 ? '🎯 Gewinner ziehen' : '🎯 Weitere Gewinner ziehen'}
                </button>
                
                {winners.length > 0 && (
                    <button
                        onClick={resetWinners}
                        style={{
                            marginTop: '12px',
                            padding: '8px 16px',
                            fontSize: '14px',
                            backgroundColor: 'transparent',
                            color: '#6c757d',
                            border: '1px solid #6c757d',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#6c757d';
                            e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#6c757d';
                        }}
                    >
                        Zurücksetzen
                    </button>
                )}

                {!canDrawMore && totalTickets > 0 && (
                    <p style={{ marginTop: '12px', color: '#6c757d' }}>
                        Alle {totalTickets} Lose wurden gezogen.
                    </p>
                )}
            </div>

            {/* Winners list */}
            {winners.length > 0 && (
                <div>
                    <h3 style={{ marginBottom: '16px', color: '#333' }}>
                        🏆 Gezogene Gewinner ({winners.length})
                    </h3>
                    
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '14px'
                        }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <th style={{ 
                                        padding: '12px 8px', 
                                        borderBottom: '2px solid #dee2e6',
                                        textAlign: 'center',
                                        fontWeight: 600,
                                        width: '60px'
                                    }}>
                                        #
                                    </th>
                                    <th style={{ 
                                        padding: '12px 8px', 
                                        borderBottom: '2px solid #dee2e6',
                                        textAlign: 'center',
                                        fontWeight: 600,
                                        width: '100px'
                                    }}>
                                        Losnummer
                                    </th>
                                    <th style={{ 
                                        padding: '12px 8px', 
                                        borderBottom: '2px solid #dee2e6',
                                        textAlign: 'left',
                                        fontWeight: 600
                                    }}>
                                        Name
                                    </th>
                                    <th style={{ 
                                        padding: '12px 8px', 
                                        borderBottom: '2px solid #dee2e6',
                                        textAlign: 'left',
                                        fontWeight: 600
                                    }}>
                                        E-Mail
                                    </th>
                                    <th style={{ 
                                        padding: '12px 8px', 
                                        borderBottom: '2px solid #dee2e6',
                                        textAlign: 'center',
                                        fontWeight: 600
                                    }}>
                                        Losnummern (gesamt)
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {winners.map((winner, index) => (
                                    <tr 
                                        key={`${winner.ticketNumber}-${winner.drawOrder}`}
                                        style={{
                                            backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                                            animation: index === winners.length - 1 ? 'highlight 1s ease-out' : 'none'
                                        }}
                                    >
                                        <td style={{ 
                                            padding: '12px 8px', 
                                            borderBottom: '1px solid #dee2e6',
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            color: '#6c757d'
                                        }}>
                                            {winner.drawOrder}
                                        </td>
                                        <td style={{ 
                                            padding: '12px 8px', 
                                            borderBottom: '1px solid #dee2e6',
                                            textAlign: 'center'
                                        }}>
                                            <span style={{
                                                display: 'inline-block',
                                                padding: '6px 12px',
                                                backgroundColor: '#ffc107',
                                                color: '#212529',
                                                borderRadius: '4px',
                                                fontWeight: 700,
                                                fontSize: '16px'
                                            }}>
                                                {winner.ticketNumber}
                                            </span>
                                        </td>
                                        <td style={{ 
                                            padding: '12px 8px', 
                                            borderBottom: '1px solid #dee2e6'
                                        }}>
                                            {winner.buyer.name || '—'}
                                        </td>
                                        <td style={{ 
                                            padding: '12px 8px', 
                                            borderBottom: '1px solid #dee2e6',
                                            maxWidth: '200px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {winner.buyer.email || '—'}
                                        </td>
                                        <td style={{ 
                                            padding: '12px 8px', 
                                            borderBottom: '1px solid #dee2e6',
                                            textAlign: 'center',
                                            color: '#6c757d'
                                        }}>
                                            {winner.buyer.ticketNumberRange}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes highlight {
                    0% { background-color: #ffc107; }
                    100% { background-color: inherit; }
                }
            `}</style>
        </div>
    );
};

export default GewinnerZiehen;
